import { useEffect, useState } from 'react'
import { Box } from '@mui/material'
import { ChartsList } from './ChartsList'
import { FormatPanel } from './FormatPanel'
import type { ChartConfig, StyledCurveReference, YAxisPosition, YAxisConfig, CurveStyling } from '@/interfaces/waveChart'

export interface ChartConfiguratorProps {
  /** Array of chart configurations */
  charts: ChartConfig[]
  
  /** Whether the configurator is disabled */
  disabled?: boolean
  
  /** Callback when chart configurations change */
  onChartsChange?: (charts: ChartConfig[]) => void
  
  /** Callback when a curve's formatting changes */
  onCurveFormatChange?: (chartId: string, curveId: string, format: CurveStyling) => void
  
  /** Callback when a curve is removed */
  onCurveRemove?: (chartId: string, curveId: string) => void
  
  /** Callback when a new chart is requested */
  onAddChart?: () => void
  
  /** Callback when a chart is deleted */
  onDeleteChart?: (chartId: string) => void
  
  /** Callback when a curve is moved between axes */
  onCurveMoveAxis?: (chartId: string, curveRef: StyledCurveReference, fromAxis: YAxisPosition, toAxis: YAxisPosition) => void
  
  /** Callback when axis configuration changes */
  onAxisConfigChange?: (chartId: string, axisId: YAxisPosition, config: YAxisConfig) => void
  
  /** Callback when chart title is changed */
  onChartTitleChange?: (chartId: string, newTitle: string) => void
  
  /** Callback when charts are reordered */
  onChartsReorder?: (fromIndex: number, toIndex: number) => void
  
  /** Callback when curves are reordered within an axis (z-order) */
  onCurveReorder?: (chartId: string, axisId: YAxisPosition, fromIndex: number, toIndex: number) => void

  /** Optional external request to open the format panel for a specific curve */
  openCurveFormat?: { chartId: string; curveId: string } | null
}

interface FormatTarget {
  type: 'curve' | 'axis'
  chartId: string
  curveId?: string
  axisId?: YAxisPosition
}

/**
 * Chart configurator component implementing the 1/3 list, 2/3 format panel layout
 * Shows chart structure with curves and axes, provides formatting controls
 */
export function ChartConfigurator({
  charts,
  disabled = false,
  onCurveFormatChange,
  onCurveRemove,
  onAddChart,
  onDeleteChart,
  onCurveMoveAxis,
  onAxisConfigChange,
  onChartTitleChange,
  onChartsReorder,
  onCurveReorder,
  openCurveFormat
}: ChartConfiguratorProps) {
  const [formatTarget, setFormatTarget] = useState<FormatTarget | null>(null)

  const handleCurveClick = (chartId: string, curveId: string) => {
    setFormatTarget({
      type: 'curve',
      chartId,
      curveId
    })
  }

  const handleAxisClick = (chartId: string, axisId: YAxisPosition) => {
    setFormatTarget({
      type: 'axis',
      chartId,
      axisId
    })
  }

  const handleBackToList = () => {
    setFormatTarget(null)
  }

  // If parent requests to open a particular curve, set the format target
  useEffect(() => {
    if (openCurveFormat) {
      const { chartId, curveId } = openCurveFormat
      setFormatTarget({ type: 'curve', chartId, curveId })
    }
    // No cleanup required; parent can control prop if needed
  }, [openCurveFormat])



  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Charts list view - takes remaining space */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <ChartsList
          charts={charts}
          disabled={disabled}
          onCurveClick={handleCurveClick}
          onAxisClick={handleAxisClick}
          onAddChart={onAddChart}
          onDeleteChart={onDeleteChart}
          onCurveMoveAxis={onCurveMoveAxis}
          onCurveRemove={onCurveRemove}
          onChartTitleChange={onChartTitleChange}
          onChartsReorder={onChartsReorder}
          onCurveReorder={onCurveReorder}
        />
      </Box>
      
      {/* Format panel - slides up from bottom when active */}
      {formatTarget && (
        <Box sx={{ 
          flexShrink: 0,
          maxHeight: '66%',
          borderTop: 1,
          borderColor: 'divider'
        }}>
          <FormatPanel
            formatTarget={formatTarget}
            charts={charts}
            onCurveFormatChange={onCurveFormatChange}
            onAxisConfigChange={onAxisConfigChange}
            onClose={handleBackToList}
          />
        </Box>
      )}
    </Box>
  )
} 
